import { getAllStories as _getAllStories, getStories as _getStories, getStoryBySlug as _getStoryBySlug } from "@cinch-nx/data-storyblok";
import { dashify } from "@cinch-labs/shared-util";
import { traceFunction, tagError, setTag } from "@core/datadog-server";
const getAllStories = traceFunction("getAllStories", _getAllStories);
const getStories = traceFunction("getStories", _getStories);
const getStoryBySlug = traceFunction("getStoryBySlug", _getStoryBySlug);
export async function getStaticProps({ params , preview =false  }) {
    try {
        let page = 1;
        let make;
        let model;
        const version = preview ? "draft" : "published";
        const resultsPerPage = 10;
        setTag("request.method", "GET");
        setTag("request.params", params);
        if (hasMakeParam(params)) {
            setTag("request.params.make", params.make);
            make = params === null || params === void 0 ? void 0 : params.make;
            console.log("Make is set to", make);
        }
        if (make === "undefined") {
            setTag("request.params.make", "undefined");
            return {
                redirect: {
                    destination: `/reviews/`,
                    permanent: true
                }
            };
        }
        if (hasModelParam(params)) {
            setTag("request.params.model", params.model);
            model = params === null || params === void 0 ? void 0 : params.model;
        }
        if (model === "undefined") {
            setTag("request.params.model", "undefined");
            return {
                redirect: {
                    destination: `/reviews/${make}`,
                    permanent: true
                }
            };
        }
        if (hasPageParam(params)) {
            setTag("request.params.page", params.page);
            page = Number(params === null || params === void 0 ? void 0 : params.page);
            let destination = "/reviews";
            if (make && model) {
                destination = `${destination}/${make}/${model}`;
            }
            if (make && !model) {
                destination = `${destination}/${make}`;
            }
            if (page === 1) {
                return {
                    redirect: {
                        destination,
                        permanent: true
                    }
                };
            }
        }
        const [story, { reviews , total: numberOfReviews  }, makeOptions, modelOptions] = await Promise.all([
            getStory({
                version
            }),
            getReviews({
                page,
                perPage: 10,
                version,
                make,
                model
            }),
            getMakeOptions(version),
            getModelOptions(make, version)
        ]);
        return {
            props: {
                isISR: true,
                preview,
                story,
                currentPage: page,
                numberOfReviews,
                selectedMake: make ?? null,
                selectedModel: model ?? null,
                makeOptions,
                modelOptions,
                resultsPerPage,
                reviews,
                layoutProps: {
                    showBorderRadius: true
                },
                generatedAt: Date.now()
            },
            revalidate: 60
        };
    } catch (error) {
        console.log(error);
        if (error instanceof Error || typeof error === "string") {
            setTag("response.statusCode", 404);
            tagError(error);
        }
        return {
            notFound: true,
            revalidate: 10
        };
    }
}
function hasPageParam(params) {
    return typeof (params === null || params === void 0 ? void 0 : params.page) === "string";
}
function hasMakeParam(params) {
    return typeof (params === null || params === void 0 ? void 0 : params.make) === "string";
}
function hasModelParam(params) {
    return typeof (params === null || params === void 0 ? void 0 : params.model) === "string";
}
async function getStory({ version  }) {
    const storyArgs = {
        version
    };
    if (version === "draft") {
        storyArgs.cv = Date.now();
    }
    const story = await getStoryBySlug("car-review-hub", storyArgs);
    if (!story || !story.content || story.content.component !== "ReviewHubPage") {
        throw new Error(`Failed to load story for 'car-review-hub' received: "${JSON.stringify(story, null, 4)}"`);
    }
    return story;
}
async function getModelOptions(make, version) {
    try {
        if (!make) {
            return [];
        }
        const storyArgs = {
            version,
            page: 1,
            starts_with: `car-reviews-v2/${dashify(make)}`,
            per_page: 100
        };
        if (version === "draft") {
            storyArgs.cv = Date.now();
        }
        const { stories  } = await getStories(storyArgs);
        if (!stories) return [];
        const tags = stories.map((story)=>story.content.model).sort();
        if (!tags) return [];
        return [
            ...new Set(tags)
        ];
    } catch (error) {
        console.error(`Error in getModelOptions: ${error}`);
        return [];
    }
}
async function getMakeOptions(version) {
    const args = {
        starts_with: "car-reviews-v2/",
        version,
        sort_by: "content.make:asc"
    };
    if (version === "draft") {
        args.cv = Date.now();
    }
    const makes = (await getAllStories(args)).stories.map((story)=>story.content.make);
    return [
        ...new Set(makes)
    ];
}
async function getReviews({ page =1 , perPage =10 , version ="published" , make , model  } = {}) {
    try {
        let starts_with = "car-reviews-v2/";
        if (make) {
            starts_with = `car-reviews-v2/${dashify(make)}`;
        }
        if (make && model) {
            starts_with = `car-reviews-v2/${dashify(make)}/${dashify(model)}`;
        }
        const reviewListArgs = {
            version,
            starts_with,
            page,
            per_page: perPage,
            sort_by: "published_at:desc"
        };
        if (version === "draft") {
            reviewListArgs.cv = Date.now();
        }
        const reviewListData = await getStories(reviewListArgs);
        const reviews = reviewListData.stories.map((story)=>toReview(story));
        return {
            reviews,
            total: reviewListData.total
        };
    } catch (error) {
        console.error(`Error in getReviews: ${error}`);
        return {
            reviews: [],
            total: 0
        };
    }
}
export const formattedHref = (url)=>url.replace(/car-reviews-v2/g, "/used-cars");
function toReview(input) {
    var ref, ref1, ref2;
    return {
        path: formattedHref(input.full_slug),
        title: input.content.title,
        heroImage: ((ref = input.content) === null || ref === void 0 ? void 0 : (ref1 = ref.gallery) === null || ref1 === void 0 ? void 0 : (ref2 = ref1[0]) === null || ref2 === void 0 ? void 0 : ref2.image) ?? null,
        date: input.published_at ?? "",
        make: input.content.make,
        model: input.content.model,
        starRating: input.content.starRating,
        id: input.uuid,
        snippet: input.content.snippet ?? ""
    };
}
